import { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Modal, Button } from 'react-bootstrap';
import Select from "../../../../AppSelect";
import Swal from "sweetalert2";

import { ValidMimeTypes, validateCSVFile } from '../../../../../utils/files.utils';
import { exportCSV } from '../../../../../utils/functionsUtils';
import { handleDownloadTemplateBulkProducts, getProductsColumnsForServiceType, getProductsHeadersForServiceType } from './uploadProduct.helpers';

import { customStyles } from '../../../../../constants/customSelectStyles';
import HelpModal from './HelpModal';

const UploadNewProductsModal = ({
    showUploadFileModal,
    handleCloseUploadFileModal,
    shippers,
    reduxUploadMassiveProductsFile,
    uploadedMassiveProductsFile,
    successfulUploadMassiveProductsFile,
    reduxGetProducts,
    reduxDownloadTemplateProductsFile,
    categories,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        control,
        setValue,
    } = useForm({
        defaultValues: {
            shipper: null,
            file: null,
        }
    });

    const fileWatcher = useRef({});
    fileWatcher.current = watch('file', '');

    const shipperValue = watch('shipper');
    const selectedShipper = shipperValue?.value
        ? shippers?.items?.find(s => s.idCompany === shipperValue.value)
        : null;
    const serviceType = selectedShipper?.serviceType;

    const [fileName, setFileName] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [showHelpModal, setShowHelpModal] = useState(false);

    const handleCloseModal = () => {
        reset();
        setFileName(null);
        setFileError(null);
        handleCloseUploadFileModal();
    };

    const onSubmitUpload = async (data) => {
        const file = data.file[0];

        if (!file) {
            setFileError('El archivo es requerido');
            return;
        }

        const expectedHeaders = getProductsHeadersForServiceType(serviceType);
        const isValid = await validateCSVFile(
            file, 
            13, 
            20,
            true, 
            expectedHeaders,
            false
        );
        if (!isValid) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                setFileError(null);
                reduxUploadMassiveProductsFile({
                    file: file,
                    idShipper: data.shipper.value,
                    onError: () => {
                        Swal.fire(
                            "Error",
                            "Hubo un error al cargar el archivo. Por favor, intente nuevamente.",
                            "error"
                        );
                    }
                });
            } catch (error) {
                console.error('Error al procesar el archivo:', error);
                setFileError('Error al procesar el archivo. Asegúrate de que sea un CSV válido.');
            }
        };

        reader.readAsText(file);
        handleCloseModal();
    };

    const handleShowUploadFileModal = () => {
        handleDownloadTemplateBulkProducts(serviceType);
    };

    useEffect(() => {
        if (!showUploadFileModal) return;

        if (fileWatcher.current != null && fileWatcher.current.length > 0) {
            setFileName(fileWatcher.current[0].name);
        }
    }, [showUploadFileModal, fileWatcher.current]);

    useEffect(() => {
        if (!successfulUploadMassiveProductsFile) return;
        reduxGetProducts({
            page: 1,
            offset: 10,
            search: "",
        });
    }, [successfulUploadMassiveProductsFile]);

    useEffect(() => {
        if (!showUploadFileModal) return;

        setFileName(null);
        setFileError(null);
        setValue('file', null);
    }, [shipperValue]);

    useEffect(() => {
        if (uploadedMassiveProductsFile && uploadedMassiveProductsFile.successFull) {
            Swal.fire(
                "Carga Exitosa",
                `El archivo se ha procesado correctamente, todos los productos han sido cargados exitosamente.`,
                "success"
            ).then(() => {
                handleCloseModal();
            });
        }

        if (uploadedMassiveProductsFile && !uploadedMassiveProductsFile.successFull) {
            const productsFailed = uploadedMassiveProductsFile.productsFailed || [];
            if (productsFailed.length > 0) {    
                Swal.fire({
                    icon: 'warning',
                    title: 'Cargue de productos con errores.',
                    html: `<p>${uploadedMassiveProductsFile.invalidProducts} de ${uploadedMassiveProductsFile.totalProducts} productos fueron inválidos.</p><br><p>Revise el archivo descargado, corrija los errores y vuelva a intentarlo. Los productos especificados en el archivo no fueron creados</p>`,
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    showConfirmButton: true,
                }).then((res) => {
                    if (res.isConfirmed) {
                        handleCloseModal(true);

                        const _now = new Date();
                        const _date = _now.toISOString().slice(0, 10).replace(/-/g, '');
                        const _time = _now.toTimeString().slice(0, 8).replace(/:/g, '');
                        const fileErrorName = `Errores_CargaProductos_${_date}_${_time}.csv`
                        const columns = getProductsColumnsForServiceType(serviceType);
                        const productoWithErrorProducts = productsFailed.map(prod => ({
                            [columns.BARCODE]:                  prod.barcode,
                            [columns.PRESENTATION]:             prod.presentation,
                            [columns.SUBCATEGORY]:              prod.subCategoryCode,
                            [columns.NAME]:                     prod.name,
                            [columns.ABBREVIATION]:             prod.abbreviation,
                            [columns.DESCRIPTION]:              prod.description,
                            [columns.EXTERNAL_ID]:              prod.sku,
                            [columns.ITEM_MASTER_SKU]:          prod.masterSku,
                            [columns.UNIT_WEIGHT]:              prod.unitWeight,
                            [columns.WEIGHT]:                   prod.weight,
                            [columns.UNIT_DIMENSIONS]:          prod.unitDimensions,
                            [columns.WIDTH]:                    prod.width,
                            [columns.HEIGHT]:                   prod.height,
                            [columns.LENGTH]:                   prod.length,
                            [columns.RECEIPT_UNIT_MEASURE]:     prod.receiptUnitMeasure,
                            [columns.DISPATCH_UNIT_MEASURE]:    prod.dispatchUnitMeasure,
                            [columns.PRICE_PER_UNIT]:           prod.pricePerUnit,
                            [columns.COMMERCIAL_PRICE]:         prod.price,
                            [columns.SHELF_LIFE_DAYS]:          prod.shelfLifeDays,
                            [columns.MAX_DISPATCH_DAYS]:        prod.maxDispatchDays ? prod.maxDispatchDays : '',
                            "razón del error": prod.reasonMessage,
                        }));
                        exportCSV(productoWithErrorProducts, fileErrorName);
                    }
                });
            }
        }
    }, [uploadedMassiveProductsFile]);

    return (
        <div>
            <Modal
                show={showUploadFileModal}
                onHide={handleCloseModal}
                onEscapeKeyDown={handleCloseModal}
                backdrop='static'
                keyboard={true}>
                <form onSubmit={handleSubmit(onSubmitUpload)}>
                    <Modal.Header closeButton>
                        <Modal.Title className='m'>
                            <div className='d-flex align-items-center'>
                                <div className='mr-2'>
                                    Cargar productos
                                </div>
                                <button
                                    title='Ver instrucciones'
                                    className="btn btn-info btn-sm btn-circle ml-2"
                                    type="button"
                                    onClick={() => setShowHelpModal(true)}
                                >
                                    <i className="fas fa-question-circle fa-sm"></i>
                                </button>
                                <button
                                    title={shipperValue ? 'Descargar plantilla CSV' : 'Primero seleccione un remitente'}
                                    className="btn btn-primary btn-sm btn-circle ml-2"
                                    type="button"
                                    disabled={!shipperValue}
                                    onClick={() => handleShowUploadFileModal()}
                                >
                                    <i className="fas fa-download fa-sm"></i>
                                </button>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='row'>
                            <div className='form-group col-md-12' style={{ display: 'block' }}>
                                <label
                                    htmlFor='shipper'
                                    className='form-label'>
                                    Remitente
                                </label>
                                <Controller
                                    name="shipper"
                                    control={control}
                                    rules={{ required: 'El remitente es requerido' }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            className={`${errors.shipper && 'is-invalid'}`}
                                            id="shipper"
                                            onChange={(value) => {
                                                field.onChange(value);
                                            }}
                                            options={
                                                shippers && Object.keys(shippers).length > 0
                                                    ? shippers.items
                                                        .filter((f) => f.isActive && f.createOrderEnabled)
                                                        .map((e, key) => ({
                                                            value: e.idCompany,
                                                            label: e.description,
                                                        }))
                                                    : []
                                            }
                                            styles={customStyles}
                                        />
                                    )}
                                />
                                {errors.shipper && (
                                    <span className="error-message">
                                        {errors.shipper.message}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group col-md-12">
                                <label htmlFor="file" className="form-label">
                                    Archivo
                                </label>
                                <div className="custom-file">
                                    <input
                                        className={`custom-file-input ${errors.file && 'is-invalid'}`}
                                        type='file'
                                        id='file'
                                        name='file'
                                        accept={ValidMimeTypes.CSV.join(',')}
                                        disabled={!shipperValue}
                                        {...register('file', { required: 'El archivo es requerido' })}
                                    />{' '}
                                    <label
                                        className='custom-file-label'
                                        htmlFor='validatedCustomFile'>
                                        {fileError ? fileError : fileName ? fileName : 'Seleccionar archivo...'}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <hr />
                        <div className="alert alert-info mb-0 py-2" style={{ fontSize: '0.85rem' }}>
                            <p className="font-weight-bold mb-1">
                                <i className="fas fa-info-circle mr-1"></i>Instrucciones
                            </p>
                            <ol className="mb-0 pl-3">
                                <li>Descarga la plantilla CSV para ver el formato correcto.</li>
                                <li>Completa todos los campos obligatorios marcados con (*).</li>
                                <li>Asegúrate de que los datos cumplan con las validaciones.</li>
                                <li>Haz clic en el icono de ayuda (<i className="fas fa-question-circle"></i>) para ver todas las validaciones.</li>
                            </ol>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant='secondary'
                            onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button
                            variant='primary'
                            type='submit'>
                            Cargar archivo
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>
            <HelpModal 
                show={showHelpModal}
                handleClose={() => setShowHelpModal(false)}
            />
        </div>
    )
}

const mapStateToProps = (state) => {
    return {
        uploadedMassiveProductsFile: state.productState.uploadedMassiveProductsFile,
        successfulUploadMassiveProductsFile: state.productState.successfulUploadMassiveProductsFile,
    }
};

const mapDispatchToProps = (dispatch) => {
    return {
        reduxUploadMassiveProductsFile: (payload) =>
            dispatch({
                type: 'UPLOAD_MASSIVE_PRODUCTS_FILE_REQUEST',
                value: payload,
            }),
        reduxGetProducts: (payload) =>
            dispatch({
                type: 'FETCH_PRODUCTS_REQUEST',
                value: payload,
            }),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadNewProductsModal);
